import { EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Component } from '@angular/core';
import { first } from 'rxjs/operators';
import { PagerOptions } from '~core/models';
import { PaginationService } from '~core/services';

/**
 * Component that build and operates a table with added pagination
 */
@Component({
  selector: 'app-paginated-table',
  templateUrl: './paginated-table.component.html'
})

export class PaginatedTableComponent implements OnInit {
  public pagerOptions: PagerOptions;
  public loadedOnce: boolean = false;
  public loading: boolean = true;

  // Pagination Inputs and Outputs
  @Input() public baseApi: string;
  @Input() public customQueries: Array<any> = [];
  @Input() public initialLoad: boolean = true;

  @Input() public page: number = 1;
  @Input() public itemsPerPage: number = 10;
  @Input() public viewList: Array<number> = [10, 20, 30];

  @Output() public dataFetchedEmitter: EventEmitter<any> = new EventEmitter<any>();

  // Table Inputs and Outputs
  @Input() public actionButtonTooltip: string = null;
  @Input() public actionButtonIcon: string = null;
  @Input() public actionButtons: Array<any> = [];

  @Output() public rowClickEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() public rowButtonClickEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() public actionButtonClickEvent: EventEmitter<any> = new EventEmitter<any>();

  @Input() public emptyStateIcon: string;
  @Input() public emptyStateText: string;
  @Input() public emptyStateButtonText: string = null;

  @Input() public columns: Array<string>;
  @Input() public keys: Array<string> | any;
  @Input() public data: Array<any> = [];

  constructor(private paginationService: PaginationService) { }

  ngOnInit(): void {
    if (this.baseApi && this.initialLoad) {
      this.fetchData(this.page, this.itemsPerPage);
    } else {
      this.loading = false;
    }
  }

  /**
   * Creates the query from the main objects
   */
  public buildQuery(page: number, itemsPerPage: number): any {
    const customQueries = [...this.customQueries];
    const pageQuery = { page };
    const listOfItemsQuery = { itemsPerPage };

    customQueries.push(pageQuery, listOfItemsQuery);
    return customQueries;
  }

  /**
   * Fetches the data and sends it to the parent component to create a modified object (if needed)
   */
  public fetchData(page: number, itemsPerPage: number): void {
    this.loading = true;
    const queryList = this.buildQuery(page, itemsPerPage);

    this.paginationService.queryPaginated(this.baseApi, queryList)
      .pipe(first())
      .subscribe(res => {
        const totalCount = res['hydra:totalItems'];
        const pager = this.paginationService.getPager(Number(totalCount), Number(page), itemsPerPage);

        this.pagerOptions = new PagerOptions(this.baseApi, this.customQueries, pager);

        this.dataFetchedEmitter.emit(res['hydra:member']);
        this.loadedOnce = true;
        this.loading = false;
      });
  }

  /**
   * Triggers a refetch of data
   */
  public refetchData(): void {
    this.fetchData(this.page, this.itemsPerPage);
  }

  /**
   * This event fires after the empty state button was clicked
   */
  public actionButtonClick(): void {
    this.actionButtonClickEvent.emit('click');
  }

  /**
   * This event fires after the button was clicked
   */
  public rowClick(element: any): void {
    this.rowClickEvent.emit(element);
  }

  /**
   * Triggers when row button is clicked
   */
  public rowButtonClick(element: any): void {
    this.rowButtonClickEvent.emit(element);
  }

  /**
   * Sets the current page of the pager
   */
  public setCurrentPage(page: number): void {
    this.fetchData(page, this.pagerOptions.pager.pageSize);
  }

  /**
   * Sets the current page of the pager
   */
  public setCurrentItems(itemsPerPage: number): void {
    this.fetchData(1, itemsPerPage);
  }

}
